import { formatDateLong, formatDateLongWithWeekday } from '@/shared/formatters';
import { RITO_LABELS } from '@/shared/constants/rito';

import type {
  ArbitragemAndamento,
  ArbitragemCampoExplicado,
  ArbitragemDocumento,
  ArbitragemPendencia,
  ArbitragemPendenciaResolvida,
} from '../types/arbitragemDetalheTypes';

interface ComplementosMockArgs {
  requerenteNome: string;
  dataCitacaoIso: string;
  agoraIso: string;
}

interface ArbitragemComplementos {
  valorCobrado: number;
  andamentos: ArbitragemAndamento[];
  documentos: ArbitragemDocumento[];
  campos: ArbitragemCampoExplicado[];
  pendencias: ArbitragemPendencia[];
  pendenciasResolvidas: ArbitragemPendenciaResolvida[];
  relatoOutraParte: string;
  prazoMedioDias: number;
}

const deslocarDias = (iso: string, dias: number): string => {
  const base = new Date(iso);
  base.setDate(base.getDate() + dias);
  return base.toISOString();
};

const montarPendenciasMock = (agoraIso: string): ArbitragemPendencia[] => [
  {
    id: 'pendencia-comprovante',
    titulo: 'Enviar o comprovante de pagamento',
    mensagem:
      'Você disse na sua resposta que pagou parte do valor. Envie o comprovante desse pagamento para eu conferir, junto com qualquer explicação que ajude a entender.',
    prazoLimiteIso: deslocarDias(agoraIso, 3),
    origemRotulo: `Mensagem do árbitro · ${formatDateLong(deslocarDias(agoraIso, -4))}`,
    documento: { id: 'pendencia-doc-determinacao', nome: 'determinacao-arbitro.pdf' },
    abreImpugnacaoDoEspecialista: false,
  },
  {
    id: 'pendencia-disponibilidade',
    titulo: 'Informar sua disponibilidade para a audiência',
    mensagem:
      'Vamos marcar a audiência do seu caso. Diga em quais dias e horários você pode participar. Se tiver algum documento que comprove uma indisponibilidade, envie junto.',
    prazoLimiteIso: deslocarDias(agoraIso, 12),
    origemRotulo: `Mensagem da secretaria · ${formatDateLong(deslocarDias(agoraIso, -7))}`,
    documento: null,
    abreImpugnacaoDoEspecialista: false,
  },
];

const montarPendenciasResolvidasMock = (agoraIso: string): ArbitragemPendenciaResolvida[] => [
  {
    id: 'pendencia-dados-contato',
    titulo: 'Confirmar seus dados de contato',
    resolvidaEmIso: deslocarDias(agoraIso, -6),
    resumoResolucao: 'dados confirmados',
  },
  {
    id: 'pendencia-termo-arbitragem',
    titulo: 'Assinar o termo de arbitragem',
    resolvidaEmIso: deslocarDias(agoraIso, -8),
    resumoResolucao: 'documento assinado',
  },
  {
    id: 'pendencia-copia-contrato',
    titulo: 'Enviar cópia do contrato citado',
    resolvidaEmIso: deslocarDias(agoraIso, -9),
    resumoResolucao: '2 arquivos enviados',
  },
  {
    id: 'pendencia-recebimento',
    titulo: 'Confirmar que recebeu a notificação',
    resolvidaEmIso: deslocarDias(agoraIso, -11),
    resumoResolucao: 'recebimento confirmado',
  },
];

// TODO: remover quando GET /arbitral/portal/citacoes/:id devolver valor cobrado,
// andamentos, documentos, campos explicativos, pendências e relato da outra parte.
export const montarComplementosMock = ({
  requerenteNome,
  dataCitacaoIso,
  agoraIso,
}: ComplementosMockArgs): ArbitragemComplementos => ({
  valorCobrado: 2500,
  prazoMedioDias: 90,
  pendencias: montarPendenciasMock(agoraIso),
  pendenciasResolvidas: montarPendenciasResolvidasMock(agoraIso),
  relatoOutraParte: `${requerenteNome} diz que houve um problema não resolvido e pediu que a Arbitralis analise o caso. Também enviou documentos para provar o que afirma.`,
  andamentos: [
    {
      id: 'mock-andamento-documento',
      numero: 3,
      ocorridoEmIso: deslocarDias(dataCitacaoIso, 15),
      titulo: `${requerenteNome} enviou um novo documento`,
      descricao: 'Uma imagem foi enviada pelo chat e a Arbitralis anexou esse arquivo ao caso.',
      autor: requerenteNome,
      icone: 'documento',
      documento: { id: 'mock-doc-chat', rotulo: 'Ver o documento enviado' },
    },
    {
      id: 'mock-andamento-recebimento',
      numero: 2,
      ocorridoEmIso: dataCitacaoIso,
      titulo: 'A Arbitralis confirmou que recebeu o pedido',
      descricao: 'Nossa equipe começou a conferir as informações e os documentos enviados.',
      autor: 'Equipe Arbitralis',
      icone: 'confirmacao',
      documento: null,
    },
    {
      id: 'mock-andamento-abertura',
      numero: 1,
      ocorridoEmIso: dataCitacaoIso,
      titulo: `${requerenteNome} abriu o caso contra você`,
      descricao:
        'Foi explicado o problema, informado quanto está sendo cobrado e enviados os primeiros documentos.',
      autor: requerenteNome,
      icone: 'abertura',
      documento: null,
    },
  ],
  documentos: [
    {
      id: 'mock-carta',
      titulo: 'Carta de notificação',
      descricao: 'Documento oficial que avisa você sobre o caso.',
      enviadoPor: 'Arbitralis',
      enviadoEmIso: deslocarDias(dataCitacaoIso, 1),
    },
    {
      id: 'mock-pedido',
      titulo: `Pedido feito por ${requerenteNome}`,
      descricao: 'O que está sendo pedido e por quê, com os detalhes do contrato.',
      enviadoPor: requerenteNome,
      enviadoEmIso: dataCitacaoIso,
    },
    {
      id: 'mock-foto',
      titulo: 'Foto enviada como prova',
      descricao: 'Imagem anexada para mostrar o problema.',
      enviadoPor: requerenteNome,
      enviadoEmIso: dataCitacaoIso,
    },
    {
      id: 'mock-contrato',
      titulo: 'Contrato assinado',
      descricao: 'O contrato em que está escrito que problemas seriam resolvidos aqui.',
      enviadoPor: requerenteNome,
      enviadoEmIso: dataCitacaoIso,
    },
  ],
  campos: [
    {
      id: 'inicio',
      rotulo: 'Quando o caso começou',
      valor: formatDateLongWithWeekday(dataCitacaoIso),
      ajuda: 'Data em que o pedido foi registrado.',
    },
    {
      id: 'forma',
      rotulo: 'Como o caso será resolvido',
      valor: 'Arbitragem',
      ajuda:
        'Um especialista neutro decide quem tem razão, sem precisar ir ao fórum. A decisão vale como uma sentença.',
    },
    {
      id: 'competencia',
      rotulo: 'Por que este caso veio para cá',
      valor: 'Estava escrito no contrato',
      ajuda: 'O contrato que você assinou diz que problemas seriam resolvidos pela Arbitralis.',
    },
    {
      id: 'rito',
      rotulo: 'Rito',
      valor: RITO_LABELS.SUMARIO,
      ajuda: '',
    },
  ],
});
